import { MetaTagsConfig } from '../hooks/useMetaTags';

export const pageMetaConfigs: Record<string, MetaTagsConfig> = {
  home: {
    title: 'Thrivebot - The Ultimate Discord Bot for Communities',
    description: 'Boost your Discord server with Thrivebot! Advanced moderation, economy system, games, engagement tools, and more. Free and premium features available.',
    keywords: 'Discord bot, community bot, moderation bot, economy bot, engagement bot, games, leveling, Thrivebot, free Discord bot, Discord server management, server tools',
    ogTitle: 'Thrivebot - Supercharge Your Discord Server',
    ogDescription: 'Transform your Discord community with Thrivebot\'s powerful features: moderation, economy, games, engagement, and more!',
    twitterTitle: 'Thrivebot - Level Up Your Discord Server',
    twitterDescription: 'Experience the best of Discord with Thrivebot! Moderation, economy, games, and more to engage your community.',
    canonical: 'https://thrivebot.in',
    ogUrl: 'https://thrivebot.in',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Thrivebot',
      description: 'Ultimate Discord bot for community management and engagement',
      applicationCategory: 'CommunicationApplication',
      operatingSystem: 'Discord',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    }
  },
  
  features: {
    title: 'Thrivebot Features - Complete Discord Bot Functionality',
    description: 'Discover all Thrivebot features: advanced moderation tools, economy system, mini-games, auto-moderation, custom commands, leveling system, and much more!',
    keywords: 'Discord bot features, moderation tools, economy system, mini games, auto-moderation, custom commands, leveling system, server management',
    ogTitle: 'Explore Thrivebot\'s Powerful Features',
    ogDescription: 'From moderation to entertainment - see why Thrivebot is the complete solution for your Discord server.',
    canonical: 'https://thrivebot.in/features',
    ogUrl: 'https://thrivebot.in/features'
  },
  
  commands: {
    title: 'Thrivebot Commands - Complete Command Reference',
    description: 'Browse all Thrivebot commands with detailed descriptions, usage examples, and permission requirements. Moderation, fun, economy, and utility commands.',
    keywords: 'Discord bot commands, moderation commands, fun commands, economy commands, utility commands, command reference, bot help',
    ogTitle: 'Complete Thrivebot Command Reference',
    ogDescription: 'Master Thrivebot with our comprehensive command guide. Find every command you need for your Discord server.',
    canonical: 'https://thrivebot.in/commands',
    ogUrl: 'https://thrivebot.in/commands'
  },
  
  pricing: {
    title: 'Thrivebot Premium - Affordable Discord Bot Pricing',
    description: 'Unlock premium Thrivebot features with our affordable pricing plans. Enhanced moderation, unlimited economy, priority support, and exclusive features.',
    keywords: 'Discord bot premium, Thrivebot pricing, premium features, Discord bot subscription, affordable bot pricing',
    ogTitle: 'Thrivebot Premium - Unlock Advanced Features',
    ogDescription: 'Get more from your Discord server with Thrivebot Premium. Affordable plans with powerful features.',
    canonical: 'https://thrivebot.in/premium',
    ogUrl: 'https://thrivebot.in/premium'
  },
  
  support: {
    title: 'Thrivebot Support - Get Help with Your Discord Bot',
    description: 'Need help with Thrivebot? Join our support server, browse documentation, or contact our team. We\'re here to help you succeed!',
    keywords: 'Discord bot support, Thrivebot help, bot documentation, Discord server support, technical assistance',
    ogTitle: 'Get Support for Thrivebot',
    ogDescription: 'Expert support for Thrivebot users. Join our community and get the help you need.',
    canonical: 'https://thrivebot.in/support',
    ogUrl: 'https://thrivebot.in/support'
  },
  
  invite: {
    title: 'Invite Thrivebot - Add to Your Discord Server',
    description: 'Ready to upgrade your Discord server? Invite Thrivebot now and start enjoying powerful moderation, economy, games, and community features!',
    keywords: 'invite Discord bot, add Thrivebot, Discord server setup, bot permissions, server management',
    ogTitle: 'Invite Thrivebot to Your Server',
    ogDescription: 'Transform your Discord community today. Add Thrivebot with just one click!',
    canonical: 'https://thrivebot.in/invite',
    ogUrl: 'https://thrivebot.in/invite'
  },
  
  blog: {
    title: 'Thrivebot Blog - Discord Tips, Updates & Guides',
    description: 'Stay updated with the latest Thrivebot news, Discord server management tips, feature updates, and community guides.',
    keywords: 'Discord blog, server management tips, bot updates, community guides, Discord news, Thrivebot blog',
    ogTitle: 'Thrivebot Blog - Discord Community Insights',
    ogDescription: 'Expert insights, tips, and updates for Discord server owners and Thrivebot users.',
    canonical: 'https://thrivebot.in/blog',
    ogUrl: 'https://thrivebot.in/blog'
  },
  
  changelogs: {
    title: 'Thrivebot Changelogs - Latest Updates & Features',
    description: 'Track all Thrivebot updates, new features, bug fixes, and improvements. Stay informed about what\'s new with your favorite Discord bot.',
    keywords: 'Discord bot updates, Thrivebot changelog, new features, bug fixes, bot improvements, release notes',
    ogTitle: 'Thrivebot Update History',
    ogDescription: 'See what\'s new with Thrivebot. Track all updates, features, and improvements.',
    canonical: 'https://thrivebot.in/changelogs',
    ogUrl: 'https://thrivebot.in/changelogs'
  },
  
  team: {
    title: 'Meet the Thrivebot Team - Discord Bot Developers',
    description: 'Meet the passionate developers and community managers behind Thrivebot. Learn about our mission to enhance Discord communities worldwide.',
    keywords: 'Thrivebot team, Discord bot developers, about us, development team, community managers',
    ogTitle: 'The People Behind Thrivebot',
    ogDescription: 'Meet our dedicated team working to make Discord communities better every day.',
    canonical: 'https://thrivebot.in/team',
    ogUrl: 'https://thrivebot.in/team'
  },
  
  faq: {
    title: 'Thrivebot FAQ - Frequently Asked Questions',
    description: 'Find answers to common questions about Thrivebot. Setup guides, troubleshooting, features, pricing, and more.',
    keywords: 'Discord bot FAQ, Thrivebot questions, bot setup help, troubleshooting, common issues',
    ogTitle: 'Thrivebot FAQ - Get Your Questions Answered',
    ogDescription: 'Quick answers to all your Thrivebot questions. From setup to advanced features.',
    canonical: 'https://thrivebot.in/faq',
    ogUrl: 'https://thrivebot.in/faq'
  },
  
  contact: {
    title: 'Contact Thrivebot - Get in Touch with Our Team',
    description: 'Have questions or need assistance? Contact the Thrivebot team through our support channels. We\'re here to help!',
    keywords: 'contact Thrivebot, Discord bot support, get in touch, customer service, technical support',
    ogTitle: 'Contact Thrivebot Support',
    ogDescription: 'Reach out to our team for support, questions, or feedback. We\'re here to help!',
    canonical: 'https://thrivebot.in/contact',
    ogUrl: 'https://thrivebot.in/contact'
  },
  
  vote: {
    title: 'Vote for Thrivebot - Support Your Favorite Discord Bot',
    description: 'Support Thrivebot by voting on bot listing sites. Help us reach more Discord communities and earn exclusive rewards!',
    keywords: 'vote Discord bot, support Thrivebot, bot listings, Discord bot votes, community support',
    ogTitle: 'Vote for Thrivebot - Show Your Support',
    ogDescription: 'Help Thrivebot grow by voting on bot lists. Earn rewards and support the community!',
    canonical: 'https://thrivebot.in/vote',
    ogUrl: 'https://thrivebot.in/vote'
  },
  
  status: {
    title: 'Thrivebot Status - Bot Uptime & Service Health',
    description: 'Check Thrivebot\'s current status, uptime statistics, and service health. Stay informed about any ongoing maintenance or issues.',
    keywords: 'Discord bot status, Thrivebot uptime, service health, bot maintenance, system status',
    ogTitle: 'Thrivebot Service Status',
    ogDescription: 'Real-time status and uptime information for Thrivebot services.',
    canonical: 'https://thrivebot.in/status',
    ogUrl: 'https://thrivebot.in/status'
  },
  
  roadmap: {
    title: 'Thrivebot Roadmap - Upcoming Features & Development',
    description: 'See what\'s coming next for Thrivebot! Explore our development roadmap, upcoming features, and community-requested improvements.',
    keywords: 'Discord bot roadmap, upcoming features, development plans, future updates, Thrivebot development',
    ogTitle: 'Thrivebot Development Roadmap',
    ogDescription: 'Discover what\'s coming next for Thrivebot. See our plans for new features and improvements.',
    canonical: 'https://thrivebot.in/roadmap',
    ogUrl: 'https://thrivebot.in/roadmap'
  },
  
  rules: {
    title: 'Thrivebot Community Rules - Terms of Use',
    description: 'Read Thrivebot\'s community rules and guidelines. Learn about proper bot usage, community standards, and acceptable behavior.',
    keywords: 'Discord bot rules, community guidelines, terms of use, acceptable behavior, bot usage rules',
    ogTitle: 'Thrivebot Community Guidelines',
    ogDescription: 'Important rules and guidelines for using Thrivebot and participating in our community.',
    canonical: 'https://thrivebot.in/rules',
    ogUrl: 'https://thrivebot.in/rules'
  },
  
  privacy: {
    title: 'Thrivebot Privacy Policy - Your Data Protection',
    description: 'Learn how Thrivebot protects your privacy and handles data. Comprehensive privacy policy covering data collection, usage, and security.',
    keywords: 'Discord bot privacy, data protection, privacy policy, data security, user privacy',
    ogTitle: 'Thrivebot Privacy Policy',
    ogDescription: 'How we protect your privacy and handle your data responsibly.',
    canonical: 'https://thrivebot.in/privacy',
    ogUrl: 'https://thrivebot.in/privacy'
  },
  
  tos: {
    title: 'Thrivebot Terms of Service - Legal Agreement',
    description: 'Thrivebot Terms of Service outlining the legal agreement between users and our service. Important information about bot usage and responsibilities.',
    keywords: 'Discord bot terms, terms of service, legal agreement, user agreement, service terms',
    ogTitle: 'Thrivebot Terms of Service',
    ogDescription: 'Legal terms and conditions for using Thrivebot services.',
    canonical: 'https://thrivebot.in/tos',
    ogUrl: 'https://thrivebot.in/tos'
  },
  
  welcome: {
    title: 'Welcome to Thrivebot - Setup Guide & Getting Started',
    description: 'Welcome to Thrivebot! Learn how to set up and configure your new Discord bot. Step-by-step guide for new users.',
    keywords: 'Discord bot setup, Thrivebot welcome, getting started, bot configuration, new user guide',
    ogTitle: 'Welcome to Thrivebot - Let\'s Get Started',
    ogDescription: 'New to Thrivebot? Let us help you get started with our comprehensive setup guide.',
    canonical: 'https://thrivebot.in/welcome',
    ogUrl: 'https://thrivebot.in/welcome'
  },
  
  refund: {
    title: 'Thrivebot Refund Policy - Premium Subscription Refunds',
    description: 'Learn about Thrivebot\'s refund policy for premium subscriptions. Information about refund eligibility, process, and timelines.',
    keywords: 'Discord bot refund, premium refund policy, subscription refund, money back guarantee',
    ogTitle: 'Thrivebot Refund Policy',
    ogDescription: 'Understand our refund policy for premium Thrivebot subscriptions.',
    canonical: 'https://thrivebot.in/refund',
    ogUrl: 'https://thrivebot.in/refund'
  },
  
  'successfully-bought': {
    title: 'Thank You for Your Purchase - Thrivebot Premium',
    description: 'Thank you for purchasing Thrivebot Premium! Your premium features are now active. Learn how to make the most of your subscription.',
    keywords: 'Thrivebot premium activated, purchase confirmation, premium features, subscription active',
    ogTitle: 'Welcome to Thrivebot Premium!',
    ogDescription: 'Your premium subscription is now active. Enjoy enhanced features and priority support!',
    canonical: 'https://thrivebot.in/successfully-bought',
    ogUrl: 'https://thrivebot.in/successfully-bought'
  },
  
  timezone: {
    title: 'Thrivebot Timezone Settings - Configure Your Server Time',
    description: 'Configure timezone settings for your Discord server with Thrivebot. Ensure accurate timestamps and scheduled events.',
    keywords: 'Discord timezone, server timezone, time settings, scheduled events, timestamp configuration',
    ogTitle: 'Configure Thrivebot Timezone Settings',
    ogDescription: 'Set up the perfect timezone configuration for your Discord server.',
    canonical: 'https://thrivebot.in/timezone',
    ogUrl: 'https://thrivebot.in/timezone'
  },
  
  'cookie-policy': {
    title: 'Thrivebot Cookie Policy - Website Cookie Usage',
    description: 'Learn about how Thrivebot uses cookies on our website. Information about cookie types, purposes, and your choices.',
    keywords: 'cookie policy, website cookies, tracking, web analytics, user preferences',
    ogTitle: 'Thrivebot Cookie Policy',
    ogDescription: 'How we use cookies to improve your experience on our website.',
    canonical: 'https://thrivebot.in/cookie-policy',
    ogUrl: 'https://thrivebot.in/cookie-policy'
  },
  
  dmca: {
    title: 'Thrivebot DMCA Policy - Copyright Protection',
    description: 'Thrivebot\'s Digital Millennium Copyright Act (DMCA) policy. Information about copyright protection and takedown procedures.',
    keywords: 'DMCA policy, copyright protection, takedown notice, intellectual property',
    ogTitle: 'Thrivebot DMCA Policy',
    ogDescription: 'Our commitment to respecting intellectual property rights and copyright protection.',
    canonical: 'https://thrivebot.in/dmca',
    ogUrl: 'https://thrivebot.in/dmca'
  },
  
  gdpr: {
    title: 'Thrivebot GDPR Compliance - European Data Protection',
    description: 'Thrivebot\'s General Data Protection Regulation (GDPR) compliance information for European users. Your rights and data protection.',
    keywords: 'GDPR compliance, European data protection, user rights, data privacy, EU compliance',
    ogTitle: 'Thrivebot GDPR Compliance',
    ogDescription: 'How we protect European users\' data and ensure GDPR compliance.',
    canonical: 'https://thrivebot.in/gdpr',
    ogUrl: 'https://thrivebot.in/gdpr'
  },
  
  ccpa: {
    title: 'Thrivebot CCPA Compliance - California Privacy Rights',
    description: 'Thrivebot\'s California Consumer Privacy Act (CCPA) compliance information. California residents\' privacy rights and data protection.',
    keywords: 'CCPA compliance, California privacy rights, consumer privacy, data protection, privacy act',
    ogTitle: 'Thrivebot CCPA Compliance',
    ogDescription: 'California residents\' privacy rights and how we protect your data under CCPA.',
    canonical: 'https://thrivebot.in/ccpa',
    ogUrl: 'https://thrivebot.in/ccpa'
  },
  
  aup: {
    title: 'Thrivebot Acceptable Use Policy - Service Guidelines',
    description: 'Thrivebot\'s Acceptable Use Policy outlining proper service usage, prohibited activities, and community standards.',
    keywords: 'acceptable use policy, service guidelines, prohibited activities, community standards, proper usage',
    ogTitle: 'Thrivebot Acceptable Use Policy',
    ogDescription: 'Guidelines for proper and acceptable use of Thrivebot services.',
    canonical: 'https://thrivebot.in/aup',
    ogUrl: 'https://thrivebot.in/aup'
  },
  
  captcha: {
    title: 'Thrivebot Captcha Verification - Security Check',
    description: 'Complete the security verification to access Thrivebot features. This helps us protect our service from automated abuse.',
    keywords: 'captcha verification, security check, bot protection, automated abuse prevention',
    ogTitle: 'Security Verification Required',
    ogDescription: 'Please complete the security check to continue using Thrivebot.',
    canonical: 'https://thrivebot.in/captcha',
    ogUrl: 'https://thrivebot.in/captcha'
  },

  dataRequest: {
    title: 'Request My Data - Thrivebot Privacy Portal',
    description: 'Request a copy of your personal data stored by Thrivebot. We comply with GDPR, CCPA, and other privacy regulations to protect your information.',
    keywords: 'data request, GDPR, CCPA, privacy rights, personal data, data export, user data',
    ogTitle: 'Request Your Thrivebot Data',
    ogDescription: 'Exercise your privacy rights and request a copy of your personal data stored by Thrivebot.',
    canonical: 'https://thrivebot.in/data-request',
    ogUrl: 'https://thrivebot.in/data-request'
  }
};
